import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import merge from 'lodash/merge';
import { CmsImageSchema, CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import { ReviewProvider } from '$constants/review-providers';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import type { ContentComponent } from '$cms/ContentComponent';
import { CarouselBase } from '$quantum/molecules/CarouselBase';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { StarRating } from '$quantum/atoms/StarRating';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { Grid, Row, Col } from '@/components/atoms/Grid';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import styles from './index.module.scss';

const additionalCarouselSettings = {
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 1,
        centerPadding: '28px',
      },
    },
  ],
};

const defaultConfiguration = {
  provider: ReviewProvider.TRUSTPILOT,
};

export const ReviewsCarouselSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subHeading: CmsShortTextSchema.optional(),
    reviewStars: z.record(CmsNumberSchema),
    reviewTitle: z.record(CmsShortTextSchema),
    reviewName: z.record(CmsShortTextSchema),
    reviewCity: z.record(CmsShortTextSchema),
    reviewText: z.record(CmsRichTextSchema),
    googleReviewsStars: CmsNumberSchema.optional(),
    googleReviewsTotalReviews: CmsNumberSchema.optional(),
    googleReviewsLink: CmsShortTextSchema.optional(),
    googleReviewsReviewsText: CmsShortTextSchema.optional(),
    googleReviewsBasedOnText: CmsShortTextSchema.optional(),
    googleReviewsLogo: CmsImageSchema.optional(),
  }),
  configuration: z
    .object({
      provider: z.nativeEnum(ReviewProvider),
    })
    .default(defaultConfiguration),
};

export type ReviewsCarouselTranslatedEntities = SchemaType<
  (typeof ReviewsCarouselSchema)['translatedEntities']
>;
export type ReviewsCarouselConfiguration = SchemaType<(typeof ReviewsCarouselSchema)['configuration']>;

/**
 * Component that renders Text, Images or Video in Carousel.<br />
 * https://www.notion.so/fixter/ReviewsCarousel-a44797f8802845ebb666b1559534b324
 */
export const ReviewsCarousel: ContentComponent<
  ReviewsCarouselTranslatedEntities,
  ReviewsCarouselConfiguration
> = function ReviewsCarousel({ translatedEntities, configuration, className }) {
  const {
    heading,
    subHeading,
    reviewStars,
    reviewTitle,
    reviewName,
    reviewCity,
    reviewText,
    googleReviewsStars,
    googleReviewsTotalReviews,
    googleReviewsLink,
    googleReviewsReviewsText,
    googleReviewsBasedOnText,
    googleReviewsLogo,
  } = translatedEntities;
  const { provider } = merge(defaultConfiguration, configuration);

  const bgColor = 'white';

  const carouselItems = useMemo<JSX.Element[]>(
    () =>
      Object.entries(reviewTitle).map(([index, itemTitle]) => {
        const title = itemTitle.value;
        const stars = reviewStars[index].number;
        const name = reviewName[index].value;
        const city = reviewCity[index].value;
        const text = renderRichTextContent(reviewText[index].content);
        const starColor = (stars <= 4.2 && '#60CA2A') || '#219653';

        return (
          <article className={styles.review} key={index}>
            <div>
              <StarRating
                provider={provider}
                stars={stars}
                starWidth={provider === ReviewProvider.TRUSTPILOT ? 24 : 20}
                starColor={starColor}
              />
              <HeadingLevels semanticLevel={3} styleLevel={6} className={styles.reviewHeading}>
                {title}
              </HeadingLevels>
              <div className={styles.reviewText}>{text}</div>
            </div>
            <div className={styles.author}>
              <strong>
                {name}, {city}
              </strong>
            </div>
          </article>
        );
      }),
    [provider, reviewCity, reviewName, reviewStars, reviewText, reviewTitle]
  );

  return (
    <Grid
      className={classNames(ReviewsCarousel.displayName, styles.component, className)}
      style={{
        // @ts-ignore Pass prop to styles
        '--bg-color': bgColor,
      }}
    >
      <Row className={styles.bleed}>
        <Col>
          <header className={styles.headings}>
            <HeadingLevels semanticLevel={2} className={styles.Heading}>
              {heading.value}
            </HeadingLevels>
            {subHeading?.value && (
              <HeadingLevels semanticLevel={3} className={styles.Subheading}>
                {subHeading.value}
              </HeadingLevels>
            )}
          </header>
          {provider === ReviewProvider.TRUSTPILOT ? (
            <TrustPilot templateId={TRUSTPILOT_TEMPLATE_ID} width="100%" height="64px" />
          ) : (
            googleReviewsStars &&
            googleReviewsTotalReviews && (
              <GoogleReviewsSummary
                className="review-summary"
                rating={googleReviewsStars?.number}
                totalReviews={googleReviewsTotalReviews?.number}
                link={googleReviewsLink?.value}
                reviewsText={googleReviewsReviewsText?.value}
                basedOnText={googleReviewsBasedOnText?.value}
                providerLogo={googleReviewsLogo}
              />
            )
          )}
          <CarouselBase additionalSettings={additionalCarouselSettings} backgroundColor={bgColor}>
            {carouselItems}
          </CarouselBase>
        </Col>
      </Row>
    </Grid>
  );
};

ReviewsCarousel.displayName = 'ReviewsCarousel';
ReviewsCarousel.schema = ReviewsCarouselSchema;
